import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  ShowChart as CashFlowIcon,
  TrendingUp as InflowIcon,
  TrendingDown as OutflowIcon,
  AccountBalance
} from '@mui/icons-material';

import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/exportUtils';

// Types
interface CashFlowData {
  period_start: string;
  period_end: string;
  opening_balance: number;
  closing_balance: number;
  opening_balance_formatted: string;
  closing_balance_formatted: string;
  operating_activities: {
    cash_from_customers: number;
    cash_to_suppliers: number;
    cash_to_employees: number;
    other_operating_cash: number;
    net_operating_cash: number;
    cash_from_customers_formatted: string;
    cash_to_suppliers_formatted: string;
    cash_to_employees_formatted: string;
    other_operating_cash_formatted: string;
    net_operating_cash_formatted: string;
  };
  investing_activities: {
    equipment_purchases: number;
    asset_sales: number;
    other_investing_cash: number;
    net_investing_cash: number;
    equipment_purchases_formatted: string;
    asset_sales_formatted: string;
    other_investing_cash_formatted: string;
    net_investing_cash_formatted: string;
  };
  financing_activities: {
    loan_proceeds: number;
    loan_repayments: number;
    owner_contributions: number;
    owner_withdrawals: number;
    net_financing_cash: number;
    loan_proceeds_formatted: string;
    loan_repayments_formatted: string;
    owner_contributions_formatted: string;
    owner_withdrawals_formatted: string;
    net_financing_cash_formatted: string;
  };
  net_cash_change: number;
  net_cash_change_formatted: string;
  monthly_breakdown: Array<{
    month: string;
    inflows: number;
    outflows: number;
    net_flow: number;
    inflows_formatted: string;
    outflows_formatted: string;
    net_flow_formatted: string;
  }>;
}

interface CashFlowReportProps {
  startDate: Date;
  endDate: Date;
  loading: boolean;
  onExport: (format: string) => void;
}

const CashFlowReport: React.FC<CashFlowReportProps> = ({
  startDate,
  endDate,
  loading,
  onExport
}) => {
  const [reportData, setReportData] = useState<CashFlowData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCashFlowData();
  }, [startDate, endDate]);

  const fetchCashFlowData = async () => {
    try {
      setError(null);
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await fetch(
        `/api/v1/reports/advanced/cash-flow?start_date=${startDateStr}&end_date=${endDateStr}`
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        throw new Error('Failed to fetch cash flow data');
      }
    } catch (error) {
      console.error('Error fetching cash flow data:', error);
      setError('Failed to load cash flow report');
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Loading Cash Flow Report...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info">
        No cash flow data available for the selected period.
      </Alert>
    );
  }

  const formatTableRow = (label: string, amount: string, isTotal = false, isSubtotal = false, indent = 0) => (
    <TableRow>
      <TableCell sx={{
        fontWeight: isTotal ? 'bold' : isSubtotal ? 'medium' : 'normal',
        fontSize: isTotal ? '1.1rem' : 'inherit',
        pl: 2 + indent
      }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{
        fontWeight: isTotal ? 'bold' : isSubtotal ? 'medium' : 'normal',
        fontSize: isTotal ? '1.1rem' : 'inherit'
      }}>
        {amount}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Report Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Cash Flow Statement
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {new Date(reportData.period_start).toLocaleDateString('en-GB')} to {new Date(reportData.period_end).toLocaleDateString('en-GB')}
          </Typography>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Opening Balance</Typography>
                  <Typography variant="h6">{reportData.opening_balance_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <InflowIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Operating Cash Flow</Typography>
                  <Typography variant="h6" color={reportData.operating_activities.net_operating_cash >= 0 ? 'success.main' : 'error.main'}>
                    {reportData.operating_activities.net_operating_cash_formatted}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CashFlowIcon color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Net Cash Change</Typography>
                  <Typography variant="h6" color={reportData.net_cash_change >= 0 ? 'success.main' : 'error.main'}>
                    {reportData.net_cash_change_formatted}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Closing Balance</Typography>
                  <Typography variant="h6">{reportData.closing_balance_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Cash Flow Statement */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Cash Flow Activity</strong></TableCell>
              <TableCell align="right"><strong>Amount</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Opening Balance */}
            {formatTableRow('Cash at Beginning of Period', reportData.opening_balance_formatted, true)}

            {/* Operating Activities */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'success.light', color: 'success.contrastText', fontWeight: 'bold', pt: 2 }}>
                OPERATING ACTIVITIES
              </TableCell>
            </TableRow>
            {formatTableRow('Cash Received from Customers', reportData.operating_activities.cash_from_customers_formatted, false, false, 1)}
            {formatTableRow('Cash Paid to Suppliers', `(${reportData.operating_activities.cash_to_suppliers_formatted})`, false, false, 1)}
            {formatTableRow('Cash Paid to Employees', `(${reportData.operating_activities.cash_to_employees_formatted})`, false, false, 1)}
            {formatTableRow('Other Operating Cash Flows', reportData.operating_activities.other_operating_cash_formatted, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} /></TableCell>
            </TableRow>
            {formatTableRow('Net Cash from Operating Activities', reportData.operating_activities.net_operating_cash_formatted, false, true)}

            {/* Investing Activities */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'info.light', color: 'info.contrastText', fontWeight: 'bold', pt: 2 }}>
                INVESTING ACTIVITIES
              </TableCell>
            </TableRow>
            {formatTableRow('Purchase of Equipment', `(${reportData.investing_activities.equipment_purchases_formatted})`, false, false, 1)}
            {formatTableRow('Sale of Assets', reportData.investing_activities.asset_sales_formatted, false, false, 1)}
            {formatTableRow('Other Investing Cash Flows', reportData.investing_activities.other_investing_cash_formatted, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} /></TableCell>
            </TableRow>
            {formatTableRow('Net Cash from Investing Activities', reportData.investing_activities.net_investing_cash_formatted, false, true)}

            {/* Financing Activities */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 'bold', pt: 2 }}>
                FINANCING ACTIVITIES
              </TableCell>
            </TableRow>
            {formatTableRow('Loan Proceeds', reportData.financing_activities.loan_proceeds_formatted, false, false, 1)}
            {formatTableRow('Loan Repayments', `(${reportData.financing_activities.loan_repayments_formatted})`, false, false, 1)}
            {formatTableRow('Owner Contributions', reportData.financing_activities.owner_contributions_formatted, false, false, 1)}
            {formatTableRow('Owner Withdrawals', `(${reportData.financing_activities.owner_withdrawals_formatted})`, false, false, 1)}
            <TableRow>
              <TableCell colSpan={2}><Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} /></TableCell>
            </TableRow>
            {formatTableRow('Net Cash from Financing Activities', reportData.financing_activities.net_financing_cash_formatted, false, true)}

            {/* Net Change */}
            <TableRow>
              <TableCell colSpan={2} sx={{ pt: 2 }}><Box sx={{ borderBottom: 2, borderColor: 'primary.main', my: 1 }} /></TableCell>
            </TableRow>
            {formatTableRow('Net Increase (Decrease) in Cash', reportData.net_cash_change_formatted, true)}
            {formatTableRow('Cash at End of Period', reportData.closing_balance_formatted, true)}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Monthly Breakdown */}
      {reportData.monthly_breakdown && reportData.monthly_breakdown.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Monthly Cash Flow Breakdown
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Month</strong></TableCell>
                    <TableCell align="right"><strong>Cash Inflows</strong></TableCell>
                    <TableCell align="right"><strong>Cash Outflows</strong></TableCell>
                    <TableCell align="right"><strong>Net Cash Flow</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.monthly_breakdown.map((month, index) => (
                    <TableRow key={index}>
                      <TableCell>{month.month}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {month.inflows_formatted}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        ({month.outflows_formatted})
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: month.net_flow >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 'medium'
                      }}>
                        {month.net_flow_formatted}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Cash Flow Analysis */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cash Flow Analysis
          </Typography>
          <Grid container spacing={2}>
            {reportData.operating_activities.net_operating_cash > 0 ? (
              <Grid item xs={12}>
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>Positive Operating Cash Flow:</strong> The business is generating cash from its core operations, which is a healthy sign.
                  </Typography>
                </Alert>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Negative Operating Cash Flow:</strong> The business is using more cash than it generates from operations. Monitor closely.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {reportData.net_cash_change < 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>Cash Decrease:</strong> Overall cash position has decreased. Consider improving collections or reducing expenses.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {reportData.closing_balance < 10000 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Low Cash Balance:</strong> Current cash balance is low. Consider securing additional funding or improving cash flow.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashFlowReport;
